import { ImageCropModal } from '@/components/cropper/ImageCropModal';
import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import Textarea from '@/components/forms/Textarea';
import { Upload } from '@/components/upload';
import { FileObject, uploadFileToS3WithPresignedUrl } from '@/lib/uploadToAWS';

import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { trimToAdminPath } from '@/lib/getAdminImagePath';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { z } from 'zod';
import { useManageGroup } from './hooks/useManageGroup';

const ManageGroupsFormSchema = z.object({
  groupName: z.string(),
  groupNameKatakana: z.string(),
  groupCoverImage: z.union([
    z.object({
      file: z.instanceof(File).or(z.any()),
      id: z.string().min(1, 'File id is required'),
      preview: z.string().url().or(z.string().startsWith('blob:')),
    }),
    z.string(),
  ]),
  description: z.string(),
});
export const ManageGroupsForm = ({
  groupId,
  defaultValues,
}: {
  groupId?: string;
  defaultValues: {
    groupName: string;
    groupNameKatakana: string;
    groupCoverImage: string;
    description: string;
  };
}) => {
  const form = useForm({
    schema: ManageGroupsFormSchema,
    mode: 'onBlur',
    defaultValues: {
      groupName: defaultValues.groupName || '',
      groupNameKatakana: defaultValues.groupNameKatakana || '',
      groupCoverImage: defaultValues.groupCoverImage || '',
      description: defaultValues.description || '',
    },
  });

  const [imagePreview, setImagePreview] = useState(defaultValues.groupCoverImage || '');
  const [cropSource, setCropSource] = useState<string | null>(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);
  const isEditMode = Boolean(groupId);
  const { mutateAsync } = useManageGroup();

  useEffect(() => {
    return () => {
      if (cropSource?.startsWith('blob:')) {
        URL.revokeObjectURL(cropSource);
      }
    };
  }, [cropSource]);

  const { t } = useDynamicLocale();

  return (
    <>
      <Form
        form={form}
        onSubmit={async (data) => {
          const imageFile = data.groupCoverImage;

          const uploadedOrExistingImage =
            typeof imageFile === 'string' ? imageFile : await uploadFileToS3WithPresignedUrl(imageFile as FileObject);

          const payload = {
            ...data,
            groupCoverImage: trimToAdminPath(uploadedOrExistingImage || ''),
          };

          if (isEditMode && groupId) {
            await mutateAsync({ ...payload, groupId });
          } else {
            await mutateAsync({ ...payload, groupId: '' });
          }
        }}
      >
        <div className="w-full space-y-4.5">
          <div className="gap-4.5 grid grid-cols-2">
            <Input
              label="configurationManagement.groupsManagement.manageGroups.groupName"
              placeholder={t('common.form.placeholder.groupName')}
              {...form.register('groupName')}
            />

            <Input
              label="configurationManagement.groupsManagement.manageGroups.groupNameKatakana"
              placeholder={t('common.form.placeholder.groupNameKatakana')}
              {...form.register('groupNameKatakana')}
            />
          </div>

          <Controller
            name="groupCoverImage"
            control={form.control}
            render={({ field, fieldState }) => (
              <Upload
                key={uploadKey}
                accept=".jpg,.jpeg,.png,.svg,.heic,.heif"
                multiple={false}
                error={fieldState.error?.message}
                defaultPreviewUrl={imagePreview}
                dropZoneClassName="h-[192px] w-full bg-surface-subtle"
                previewClassName="w-full object-cover h-[192px]"
                label="configurationManagement.groupsManagement.manageGroups.groupCoverPhoto"
                onFilesChange={(files) => {
                  const selected = files?.[0];

                  if (!selected || typeof selected === 'string') {
                    field.onChange('');
                    return;
                  }

                  const rawFile = selected.file as File;
                  const localPreview = selected.preview || URL.createObjectURL(rawFile);
                  setCropSource(localPreview);
                  setIsCropOpen(true);
                }}
                onDefaultPreviewRemove={() => {
                  setImagePreview('');
                  form.setValue('groupCoverImage', '', {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
              >
                <div className="text-muted-foreground text-caption font-medium">
                  <Translation id="storeManagement.premiumStores.documents.imageNote" />
                </div>
              </Upload>
            )}
          />

          <Textarea
            label="configurationManagement.groupsManagement.manageGroups.description"
            className="resize-none"
            placeholder={t('common.form.placeholder.description')}
            {...form.register('description')}
          />
        </div>
      </Form>

      <ImageCropModal
        open={isCropOpen}
        imageSrc={cropSource}
        type="store"
        onClose={() => {
          setIsCropOpen(false);

          if (cropSource?.startsWith('blob:')) {
            URL.revokeObjectURL(cropSource);
          }

          setCropSource(null);
          setUploadKey((k) => k + 1);
        }}
        onCropDone={(croppedFile, previewUrl) => {
          const croppedFileObject: FileObject = {
            file: croppedFile,
            id: `${Date.now()}`,
            preview: previewUrl,
          };

          if (cropSource?.startsWith('blob:')) {
            URL.revokeObjectURL(cropSource);
          }

          setImagePreview(previewUrl);

          form.setValue('groupCoverImage', croppedFileObject, {
            shouldValidate: true,
            shouldDirty: true,
          });

          setCropSource(null);
          setIsCropOpen(false);
          setUploadKey((k) => k + 1);
        }}
      />
    </>
  );
};
